import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, redirect, useNavigate, useSearch } from "@tanstack/react-router";
import { AlertCircle, KeyRound, ShieldCheck } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { apiRequest, ApiError, AuthRequiredError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useLocalLogin } from "@/lib/hooks/auth";
import { LoginRequestSchema, type LoginRequest } from "@/lib/schemas/auth";

const SearchSchema = z
  .object({
    return_to: z.string().optional(),
  })
  .strict();

export const Route = createFileRoute("/system")({
  validateSearch: SearchSchema.parse,
  // If already authenticated (e.g. SAML/OIDC redirected back here via RelayState),
  // don't show the login form — bounce into the app. Otherwise a logged-in user
  // lands on the login page and thinks login failed.
  beforeLoad: async ({ search }) => {
    let authed = false;
    try {
      await apiRequest<unknown>("/auth/sessions");
      authed = true;
    } catch (err) {
      if (!(err instanceof AuthRequiredError)) throw err;
    }
    if (authed) {
      const rt = search.return_to;
      const target = rt && rt.startsWith("/") && rt !== "/system" && rt !== "/login" ? rt : "/";
      throw redirect({ to: target });
    }
  },
  component: SystemLoginPage,
});

/**
 * System login page (public, no auth guard).
 *
 * ADR-0097 item 1: `/system` is the ADMIN/SUPERUSER entry — local Argon2id
 * password (bootstrap + break-glass) + SAML Google. FortiWeb restricts this
 * path and the admin auth endpoints (`/auth/login`, `/auth/saml/*`) to
 * internal IPs (docs/ops/12-system-access-fortiweb.md). Tenants use the root
 * `/login` (Keycloak OIDC).
 *
 * Vedi: apps/api/src/dpanel_api/auth/routes/{password,saml}.py.
 */
export function SystemLoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { return_to } = useSearch({ from: "/system" });

  const mutation = useLocalLogin();

  const form = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values: LoginRequest): Promise<void> {
    try {
      await mutation.mutateAsync(values);
      const target = return_to && return_to.startsWith("/") ? return_to : "/";
      void navigate({ to: target });
    } catch {
      // Error displayed via mutation.error below; do not throw upward
    }
  }

  function startSamlLogin(): void {
    const target = encodeURIComponent(return_to ?? window.location.pathname);
    window.location.assign(`/api/v1/auth/saml/login?return_to=${target}`);
  }

  const errorMessage = resolveErrorMessage(mutation.error, t);

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-3 pb-2">
          <div className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-primary">
            din<span className="italic text-accent">o</span>va
          </div>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-muted-foreground" aria-hidden />
            {t("system.title")}
          </CardTitle>
          <CardDescription>{t("system.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form className="space-y-3" onSubmit={form.handleSubmit(onSubmit)} noValidate>
            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs font-medium">
                {t("login.local.email_label")}
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                autoFocus
                disabled={mutation.isPending}
                {...form.register("email")}
              />
              {form.formState.errors.email && (
                <p className="text-xs text-destructive" role="alert">
                  {t(form.formState.errors.email.message ?? "validation.email_invalid")}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="password" className="text-xs font-medium">
                {t("login.local.password_label")}
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                disabled={mutation.isPending}
                {...form.register("password")}
              />
              {form.formState.errors.password && (
                <p className="text-xs text-destructive" role="alert">
                  {t(form.formState.errors.password.message ?? "validation.password_min")}
                </p>
              )}
            </div>
            {errorMessage && (
              <div
                className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
                role="alert"
              >
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>{errorMessage}</span>
              </div>
            )}
            <Button type="submit" className="w-full gap-2" size="lg" disabled={mutation.isPending}>
              <KeyRound className="size-4" />
              {mutation.isPending ? t("login.local.submitting") : t("login.local.submit")}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              <Link to="/forgot-password" className="underline underline-offset-2 hover:text-foreground">
                {t("login.forgot_password")}
              </Link>
            </p>
          </form>

          <div className="flex items-center gap-3 py-1">
            <Separator className="flex-1" />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("login.divider")}
            </span>
            <Separator className="flex-1" />
          </div>

          <Button
            type="button"
            onClick={startSamlLogin}
            variant="outline"
            className="w-full gap-2"
            size="lg"
            disabled={mutation.isPending}
          >
            <ShieldCheck className="size-4" />
            {t("system.saml_button")}
          </Button>
          <p className="text-center text-xs text-muted-foreground">{t("system.saml_hint")}</p>

          <p className="pt-2 text-center text-xs text-muted-foreground">
            <Link
              to="/login"
              search={return_to ? { return_to } : undefined}
              className="underline underline-offset-2 hover:text-foreground"
            >
              {t("system.tenant_link")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}

function resolveErrorMessage(err: Error | null, t: (key: string) => string): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const kind = err.problem.kind;
    if (kind === "invalid_credentials") return t("login.error.invalid_credentials");
    if (kind === "account_locked") return t("login.error.account_locked");
    if (kind === "account_suspended") return t("login.error.account_suspended");
    if (kind === "rate_limit_exceeded") return t("login.error.rate_limited");
    return err.problem.detail ?? err.message ?? t("login.error.generic");
  }
  return err.message || t("login.error.generic");
}
